import { centsToDollars } from "@outschool/business-rules";
import React from "react";

import pkg from "../../lib/pkg";
import { AnalyticsError } from "../../lib/util";
import { IntegrationCategory } from "../../providers/AnalyticsContext";
import useTrackEvent from "../useTrackEvent";
import { mapActivityListingToProduct, mapGiftCardToProduct } from "./util";

import type { ActivityListing, GiftCard, Products, Product } from "./types";

const EVENT_NAME = "Checkout Started";

interface CheckoutStartedProperties {
  order_id: string;
  affiliation: string;
  value: number;
  revenue?: number;
  shipping?: number;
  tax?: number;
  discount?: number;
  coupon?: string;
  currency: string;
  products: Products;
}

interface TrackCheckoutStartedProperties {
  activityListing?: ActivityListing;
  giftCard?: GiftCard;
  orderSlugId: string;
  currencyCode: string;
  totalCents: number;
}

export default function useCheckoutStarted() {
  const trackAdEvent = useTrackEvent(IntegrationCategory.Advertising);
  const [trackedOrderId, setTrackedOrderId] = React.useState<string>("");

  function track({
    activityListing,
    giftCard,
    orderSlugId,
    currencyCode,
    totalCents,
  }: TrackCheckoutStartedProperties) {
    try {
      let product: Product | undefined = undefined;

      if (!!activityListing) {
        product = mapActivityListingToProduct(activityListing);
      } else if (!!giftCard) {
        product = mapGiftCardToProduct({
          ...giftCard,
          uid: "",
          amountCents: totalCents,
        });
      }

      if (!product || orderSlugId === trackedOrderId) {
        return;
      }

      const properties: CheckoutStartedProperties = {
        order_id: orderSlugId,
        affiliation: "Outschool",
        value: centsToDollars(totalCents),
        currency: currencyCode,
        products: [product],
      };

      trackAdEvent(EVENT_NAME, properties);
      setTrackedOrderId(orderSlugId);
    } catch (error) {
      pkg.onError(new AnalyticsError(EVENT_NAME, error));
    }
  }

  return React.useCallback(track, [trackAdEvent, trackedOrderId]);
}
